// Uso do AppCel — Onda App Metricas (25/08/2026).
//
// So `perfil === 'admin'` chega aqui (o botao que abre este modal ja esconde a
// porta pra quem nao e; a rota do backend recusa de novo, que e' quem protege de
// verdade). Mesma regua do Diario do Usuario ([DA-411]): gerente nao ve.
//
// ⚠️ **O rodape honesto nao e' enfeite, e' requisito** (ver os dois paragrafos no
// fim do corpo): "ultimo acesso" no AppCel MENTE — so e' escrito no login, e o
// token dura 30 dias renovados sozinho. O que esta na tela e' a ultima ATIVIDADE
// real, calculada no servidor a partir de quatro fontes (mensagem, orcamento,
// agenda, e a sonda `app_acessos`). Rastro parcial que se apresenta como completo
// e' pior que rastro nenhum.
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { formatDataSP, formatDataHoraSP } from '@fiodecobre/shared';
import { Modal } from '../../../components/Modal';
import { StatusBadge } from '../../../components/StatusBadge';
import { api } from '../../../lib/api';
import { useToast } from '../../../components/Toast';

interface ContaMetrica {
  id: number;
  nome: string;
  interno: boolean;
  criado_em: string;
  ultima_atividade: string | null;
  nunca_teve_atividade: boolean;
  dias_parado: number;
  visitas: number;
  orcamentos: number;
  compromissos: number;
  mensagens: number;
  aviso_ligado: boolean;
  tem_ficha: boolean;
}

interface RespostaMetricas {
  desde: string | null;
  quem_sumiu: ContaMetrica[];
  usando: { clientes: ContaMetrica[]; casa: ContaMetrica[] };
  ferramentas: { clientes: { area: string; total: number }[]; casa: { area: string; total: number }[] };
}

const ROTULO_AREA: Record<string, string> = {
  home: 'Inicio',
  chat: 'Chat',
  orcamentos: 'Orcamentos',
  agenda: 'Agenda',
  // ⚠️ As TRES areas do Financeiro medem coisas diferentes, e os rotulos precisam
  // deixar isso obvio pra quem le o grafico: `financeiro` = abriu Contas a Pagar,
  // `notas_fiscais` = abriu o acervo (aba nova, 26/08/2026), `nota` = BAIXOU um PDF.
  // O rotulo antigo de `nota` era "Nota (PDF)", que ao lado de "Notas Fiscais" leria
  // como a mesma coisa escrita de dois jeitos.
  financeiro: 'Contas a Pagar',
  notas_fiscais: 'Notas Fiscais',
  nota: 'Baixou a nota',
};

// Quantos dias sem atividade fazem um cliente contar como "sumido". Sem esse
// corte o bloco listaria TODA conta de cliente — inclusive quem usou o app
// ontem —, e uma lista que sai igual com qualquer dado nao distingue nada.
const DIAS_SUMIU = 7;

// ⚠️ Carimbo na tela sai dos helpers da casa, nunca de `new Date(x)` cru: a API
// devolve as duas grafias (texto do Postgres com `-03` no SQL cru, ISO com `Z`
// pelo Drizzle) e formatacao caseira acerta uma e erra a outra em 3h.
function dataCurta(iso: string | null): string {
  return iso ? formatDataSP(iso) : '—';
}

function dataHora(iso: string | null): string {
  return iso ? formatDataHoraSP(iso) : '—';
}

export function ModalAppMetricas({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const toast = useToast();
  const [dados, setDados] = useState<RespostaMetricas | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    setCarregando(true);
    api
      .get<RespostaMetricas>('/api/app-metricas')
      .then((r) => vivo && setDados(r))
      .catch((e: any) => vivo && toast.erro(e.message ?? 'Falha ao carregar o uso do AppCel'))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // Entram os parados ha DIAS_SUMIU dias — E quem NUNCA usou desde o cadastro,
  // por poucos dias que faca. Esse nao esta "parado": ele nunca chegou, e' o
  // sinal de abandono mais forte que a tela tem, e esperar uma semana pra
  // mostrar so atrasa o unico convite que ainda adianta alguma coisa.
  const parados = (dados?.quem_sumiu ?? []).filter(
    (c) => c.nunca_teve_atividade || c.dias_parado >= DIAS_SUMIU,
  );

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Uso do AppCel"
      telaCheia
      // Botao de fechar TAMBEM no rodape (DA-458) — o X do cabecalho nao e' o unico
      // caminho de saida, e o Esc nao existe no tablet.
      rodape={
        <button type="button" className="btn-secundario" onClick={onFechar}>
          Fechar
        </button>
      }
    >
      {carregando || !dados ? (
        <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-color)' }}>
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 1100 }}>
          {/* (a) QUEM SUMIU — o bloco mais importante, vai primeiro. */}
          <section>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={16} style={{ color: 'var(--primary)' }} /> Quem sumiu
            </h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted, #6b7280)', marginBottom: 10 }}>
              So clientes parados ha {DIAS_SUMIU} dias ou mais, mais quem nunca usou desde o cadastro (as contas da
              casa ficam de fora). Ordenado do mais parado pro mais recente.
            </p>
            {parados.length === 0 ? (
              <p className="muted">
                {dados.quem_sumiu.length === 0
                  ? 'Nenhuma conta de cliente cadastrada ainda.'
                  : `Ninguem parado ha ${DIAS_SUMIU} dias ou mais, e ninguem cadastrado sem nunca usar — os ${dados.quem_sumiu.length} clientes usaram o app nesta semana.`}
              </p>
            ) : (
              <TabelaScroll>
                <table className="tabela" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>NOME</th>
                      <th style={{ textAlign: 'left' }}>ENTROU EM</th>
                      <th style={{ textAlign: 'left' }}>ULTIMA ATIVIDADE</th>
                      <th style={{ textAlign: 'right' }}>DIAS PARADO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parados.map((c) => (
                      <tr key={c.id}>
                        <td>{c.nome}</td>
                        <td>{dataCurta(c.criado_em)}</td>
                        <td>
                          {c.nunca_teve_atividade ? (
                            // "desligado/pendente" da casa: fundo solido #f97316 + texto preto —
                            // nunca uma var de tema (--grid-header-bg resolve DARK no Azul Royal,
                            // e o texto herdado tambem sai dark: dark-on-dark, achado ao vivo).
                            <span
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 12.5, fontWeight: 600,
                                background: '#f97316', color: '#000', borderRadius: 4, padding: '2px 7px',
                              }}
                            >
                              <AlertTriangle size={12} /> nunca usou desde o cadastro
                            </span>
                          ) : (
                            dataHora(c.ultima_atividade)
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{c.dias_parado}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabelaScroll>
            )}
          </section>

          {/* (b) QUEM ESTA USANDO — cliente e casa, NUNCA somados. */}
          <section>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Quem esta usando</h3>
            <BlocoUsando titulo={`Clientes (${dados.usando.clientes.length})`} contas={dados.usando.clientes} />
            <div style={{ height: 18 }} />
            <BlocoUsando titulo={`Da casa (${dados.usando.casa.length})`} contas={dados.usando.casa} />
          </section>

          {/* (c) QUAIS FERRAMENTAS */}
          <section>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Quais ferramentas</h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted, #6b7280)', marginBottom: 10 }}>
              Visitas por area {dados.desde ? `desde ${dataCurta(dados.desde)}` : '— a medicao comeca hoje'}.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
              <BlocoFerramentas titulo="Clientes" linhas={dados.ferramentas.clientes} />
              <BlocoFerramentas titulo="Da casa" linhas={dados.ferramentas.casa} />
            </div>
          </section>

          {/* RODAPE HONESTO — requisito, nao enfeite. */}
          <p style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', lineHeight: 1.7, borderTop: '1px solid var(--panel-border, #e5e7eb)', paddingTop: 14 }}>
            {dados.desde
              ? `Visitas medidas desde ${dataCurta(dados.desde)}. O que aconteceu antes disso nao foi registrado.`
              : 'A medicao de visitas comeca hoje. O que aconteceu antes disso nao foi registrado.'}
            <br />
            "Ultimo acesso" nao sai do login: o token vale 30 dias, entao quem usa o app todo dia pode nunca mais
            logar. O que esta na tela e a ultima ATIVIDADE.
          </p>
        </div>
      )}
    </Modal>
  );
}

function TabelaScroll({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
      {children}
    </div>
  );
}

function BlocoUsando({ titulo, contas }: { titulo: string; contas: ContaMetrica[] }) {
  return (
    <div>
      <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, opacity: 0.85 }}>{titulo}</h4>
      {contas.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>Nenhuma conta neste grupo.</p>
      ) : (
        <TabelaScroll>
          <table className="tabela" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>NOME</th>
                <th style={{ textAlign: 'left' }}>ENTROU EM</th>
                <th style={{ textAlign: 'left' }}>ULTIMA ATIVIDADE</th>
                <th style={{ textAlign: 'right' }}>VISITAS</th>
                <th style={{ textAlign: 'right' }}>ORCAMENTOS</th>
                <th style={{ textAlign: 'right' }}>COMPROMISSOS</th>
                <th style={{ textAlign: 'right' }}>MENSAGENS</th>
                <th style={{ textAlign: 'left' }}>AVISO</th>
                <th style={{ textAlign: 'left' }}>FICHA</th>
              </tr>
            </thead>
            <tbody>
              {contas.map((c) => (
                <tr key={c.id}>
                  <td>{c.nome}</td>
                  <td>{dataCurta(c.criado_em)}</td>
                  <td>{c.nunca_teve_atividade ? '— nunca usou' : dataHora(c.ultima_atividade)}</td>
                  <td style={{ textAlign: 'right' }}>{c.visitas}</td>
                  <td style={{ textAlign: 'right' }}>{c.orcamentos}</td>
                  <td style={{ textAlign: 'right' }}>{c.compromissos}</td>
                  <td style={{ textAlign: 'right' }}>{c.mensagens}</td>
                  <td><StatusBadge ativo={c.aviso_ligado} labelAtivo="Ligado" labelInativo="Desligado" /></td>
                  <td><StatusBadge ativo={c.tem_ficha} labelAtivo="Vinculada" labelInativo="Sem ficha" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabelaScroll>
      )}
    </div>
  );
}

function BlocoFerramentas({ titulo, linhas }: { titulo: string; linhas: { area: string; total: number }[] }) {
  const max = Math.max(1, ...linhas.map((l) => l.total));
  return (
    <div>
      <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, opacity: 0.85 }}>{titulo}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {linhas.map((l) => (
          <div key={l.area} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            {/* ⚠️ 104 e nao 90: os rotulos cresceram em 26/08/2026 ("Contas a Pagar",
                "Notas Fiscais") e a 12,5px eles nao cabiam nos 90 — o nome quebraria
                em duas linhas e desalinharia a barra ao lado. */}
            <span style={{ width: 104, flexShrink: 0 }}>{ROTULO_AREA[l.area] ?? l.area}</span>
            <div style={{ flex: 1, background: 'var(--grid-header-bg, #f3f4f6)', borderRadius: 4, height: 16, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${(l.total / max) * 100}%`,
                  height: '100%',
                  background: 'var(--primary, #b45309)',
                  borderRadius: 4,
                  minWidth: l.total > 0 ? 3 : 0,
                }}
              />
            </div>
            <span style={{ width: 32, textAlign: 'right', fontWeight: 700, flexShrink: 0 }}>{l.total}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
